import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Collapse, Alert, AlertTitle, Box } from '@mui/material';
import { EActionType, EAction, RoleFormFields, EDataTableType } from '../../../data/Constants';
import { getRoles, updateRole, createRole, getRoleDetails } from '../../../store/states/RoleSlice';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import { AppDispatch, AppState, AppStore } from '../../../store';
import { resetAlertMessage, setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage } from '../../ErrorMessage';
import { resetError } from '../../../store/states/ErrorSlice';
import { GenericSimpleForm } from '../../../components/Layouts/GenericSimpleForm';
import { TFunction } from 'i18next';

import type { IAPIResponse, IError, IManipulationProps, IRole } from '../../../../types';

//To call Create and Update apis
function callCreateOrUpdateApi(
  manipulationType: string,
  formData: IRole,
  t: TFunction,
  dispatch: AppDispatch
) {
  if (manipulationType === EActionType.Edit) {
    dispatch(
      updateRole({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.update'),
        t('messages.success.update'),
        dispatch
      );
    });
  } else {
    dispatch(
      createRole({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  }
}

function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
) {
  const roles = AppStore.getState().administration.roles;
  if (res?.data) {
    dispatch(
      getRoles({
        page: roles.currentPage,
        limit: roles.recordsPerPage,
        searchCode: roles.searchKey,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
}

const handleError = (error: IError, setDisplayAlertMessage: (show: boolean) => void) => {
  if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
    setDisplayAlertMessage(true);
  }
};

const loadFormData = async (
  props: IManipulationProps,
  setInitialRoleData: (data: IRole) => void,
  dispatch: AppDispatch
) => {
  let roleDetails = await dispatch(getRoleDetails({ request: [props.id] })).unwrap();

  if ('data' in roleDetails && roleDetails.data) {
    setInitialRoleData(roleDetails.data[0]);
  }
};

export const RoleManipulationForm = (props: IManipulationProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const error = useSelector((state: AppState) => state.error);
  const [displayAlertMessage, setDisplayAlertMessage] = useState(false);
  const initialData: IRole = {
    id: '',
    name: '',
    description: '',
    isDefault: false,
    isActive: false,
  };
  const [initialRoleData, setInitialRoleData] = useState(initialData);

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setInitialRoleData, dispatch);
    }
  }, []);

  useEffect(() => {
    handleError(error, setDisplayAlertMessage);
  }, [error]);

  const onSubmit: SubmitHandler<IRole> = (data) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi(manipulationType, data, t, dispatch);
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(resetAlertMessage());
    dispatch(resetError());
    setDisplayAlertMessage(false);
  };

  const manipulationType = props.type;

  return (
    <Box className="manipulationBox">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert
            data-testid="role-alert-message"
            className="errorMessage"
            severity="error"
            onClose={handleClose}
          >
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <GenericSimpleForm
          formFields={RoleFormFields}
          initialData={initialRoleData}
          onSubmit={onSubmit}
          manipulationType={manipulationType}
          setDisplayAlertMessage={setDisplayAlertMessage}
          type={EDataTableType.Roles}
          uniqueKey="name"
        />
      </Box>
    </Box>
  );
};
